export const ORGANIZATION_ADMIN_GUIDE = `# Organization Admin Guide

## Overview

VoiceThera's Organization Accounts enable companies, nonprofits, schools, and teams to provide personalized wellness programs to employees as a benefit. This guide covers everything organization administrators need to set up, manage, and optimize their wellness program.

**Key Capabilities:**
- Create and manage organization profile with biography, values, and culture
- Invite and onboard employees
- Organize employees into departments
- Purchase and allocate credits to employees
- Grant external practitioner access
- Track usage analytics and ROI

---

## Getting Started

### Creating Your Organization Account

1. Navigate to **[/organization/signup](/organization/signup)**
2. Complete the organization signup form:
   - Organization name
   - Organization type (Corporate, Nonprofit, Educational, Healthcare, Government, Other)
   - Industry
   - Size (1-10, 11-50, 51-200, 201-1000, 1000+)
   - Your role (Founder/CEO, HR Manager, Wellness Coordinator, etc.)
3. Verify your email
4. You are now the **organization owner** (full admin access)

### Initial Setup Checklist

After creating your organization:

✅ **Complete organization profile** (biography, mission, values)
✅ **Create departments** (at least one required)
✅ **Purchase initial credit pack**
✅ **Invite first employees**
✅ **Set employee allowances**
✅ **(Optional) Grant practitioner access** if using external practitioners

---

## Organization Profile

### Organization Biography (Critical)

Your organization biography is **the foundation** for contextualized employee sessions. It should comprehensively describe:

**What to include:**
- Company history and founding story
- Mission and purpose
- Core values and how they're lived
- Culture and work environment
- Wellness philosophy and commitment to employee wellbeing
- Benefits and support programs
- Leadership approach
- What makes your organization unique

**Length:** Up to 5000 characters (use it!)

**Markdown Support:**

You can use markdown formatting to structure your biography:

\`\`\`markdown
## Our Story
Founded in 2015, TechCorp started with a mission to...

## Core Values
- **Champion Belonging**: Everyone's voice matters
- **Do the Right Thing**: Ethics over expediency
- **Growth Mindset**: Embrace challenges as learning

## Our Culture
We believe in [your culture description]...

## Wellness Commitment
| Program | Description |
|---------|-------------|
| Mental Health Days | 5 per year, no questions asked |
| Therapy Coverage | $100/session, 12 sessions/year |
| Wellness Stipend | $500 annually for fitness, meditation, etc. |
\`\`\`

**Preview before saving** using the Preview/Edit toggle.

**Why this matters:**
Employee sessions will reference your biography, values, and mission. Rich, detailed biographies create deeply personalized sessions that feel authentically connected to your organization.

### Mission Statement

Your organization's purpose and reason for existing.

**Example:**
> "To empower people with technology that amplifies their potential while protecting their humanity."

### Core Values

Array of 3-8 core values that guide your organization.

**Examples:**
- Champion Belonging
- Do the Right Thing
- Embrace Curiosity
- Default to Transparency
- Bias Toward Action
- Lead with Empathy

**Why this matters:**
Employee sessions will explicitly connect personal struggles to these values, showing how organizational support is authentic and aligned with stated principles.

### Culture Description

How your organization actually operates day-to-day.

**Example:**
> "We're a distributed team that values asynchronous communication and deep work. We celebrate wins publicly and learn from failures openly. Work-life balance isn't a perk—it's a requirement. We measure impact, not hours."

### Wellness Philosophy

Your organization's approach to employee wellbeing.

**Example:**
> "We believe mental health is foundational to innovation. Burnout isn't a badge of honor—it's a systemic failure we actively prevent. We invest in employee wellness because it's the right thing to do, and because thriving employees build better products."

---

## Organization Dashboard

### Dashboard Tabs

**Overview:**
- Quick stats (total employees, active sessions this month, credit balance)
- Recent activity feed
- Upcoming allowance reset dates

**Employees:**
- List of all employees (active and inactive)
- Invitation status tracking
- Employee profile management
- Session history per employee

**Departments:**
- Department list with employee counts
- Create, edit, and manage departments
- Assign department heads

**Credits & Usage:**
- Credit balance and allocation
- Purchase credit packs
- Set employee allowances
- Usage analytics and burn rate
- Transaction logs

**Settings:**
- Organization profile editing
- Admin management (add/remove admins)
- Practitioner access control
- Billing information

---

## Department Management

### Why Departments Matter

Departments enable:
- Organizational structure and reporting
- Departmental session themes and goals
- Analytics by department
- Department-specific wellness initiatives

**Every employee must belong to at least one department.**

### Creating Departments

1. Navigate to **Departments** tab
2. Click **"Create Department"**
3. Enter:
   - Department name (e.g., "Engineering", "Customer Success", "Finance")
   - Department description (optional)
   - Department head (optional - select an employee)
4. Click **"Create"**

### Department Descriptions

Describe the department's function, challenges, and culture:

**Example (Engineering Department):**
> "Our engineering team builds the core product, balancing innovation with technical debt. The team often faces tight deadlines and scope creep, leading to stress around code quality vs. shipping fast. We value thoughtful problem-solving, collaborative code review, and sustainable pace."

Department descriptions can inform session content when specified by employees.

### Assigning Department Heads

Department heads are employees with visibility into their department's wellness metrics:
- View department-wide session usage
- See (anonymized) department trends
- Encourage participation in wellness programs

**To assign:**
1. Edit department
2. Select employee from **"Department Head"** dropdown
3. Save

**Permissions:**
Department heads do NOT have admin privileges. They see department analytics but cannot manage employees or credits.

### Multi-Department Employees

Employees can belong to multiple departments:
- Assign primary department (used in most contexts)
- Add secondary departments
- Useful for cross-functional roles (e.g., Engineering Manager in both Engineering and Leadership)

**To assign:**
1. Navigate to employee profile
2. Scroll to **"Departments"** section
3. Add departments, designate one as primary

---

## Employee Management

### Invitation System (Asynchronous Onboarding)

VoiceThera uses an **invitation-based system** that does not require pre-existing accounts.

**How it works:**
1. Admin invites employee by email
2. Invitation email sent with accept link
3. Employee clicks link (may need to create account or sign in)
4. Employee accepts invitation → joins organization
5. Employee appears in organization dashboard

**Benefits:**
- Reduces onboarding friction
- Employees control their own account creation
- Supports existing VoiceThera users and new users
- Maintains user autonomy and privacy

### Sending Invitations

1. Navigate to **Employees** tab
2. Click **"Invite Employee"**
3. Enter:
   - **Email** (required) - must be work email
   - **First Name** (optional) - used in invitation email
   - **Last Name** (optional)
   - **Employee Role** (optional) - e.g., "Software Engineer", "HR Manager"
   - **Custom Message** (optional) - personalized welcome message
4. Click **"Send Invitation"**

**Example custom message:**
> "Welcome to TechCorp! We're excited to offer you personalized wellness sessions through VoiceThera. These sessions are designed specifically for you, incorporating our company values and your individual needs. Your mental health matters to us—please take advantage of this benefit."

### Invitation Status Tracking

Invitations have statuses:
- **Pending**: Invitation sent, not yet accepted
- **Accepted**: Employee joined organization
- **Expired**: Invitation not accepted within 7 days
- **Revoked**: Admin cancelled invitation

**To resend expired invitations:**
1. Navigate to **Employees** tab → **Invitations** sub-tab
2. Find expired invitation
3. Click **"Resend"** (generates new link)

### Employee Profile Fields

#### Set by Admin During Invitation
- Email
- First Name / Last Name (optional)
- Employee Role

#### Owned by Employee (Cannot Be Pre-Set by Admin)
- Employee Biography
- Preferred Name
- Name Usage Preference (preferred, first name, full name)

**Why this distinction matters:**
Employees own their personal information and wellness data. Admins provide organizational context, but employees control how they present themselves and what personal details they share.

### Name Display Preferences

Employees control how their name appears throughout the system:

**Options:**
1. **Preferred Name**: Uses \`preferred_name\` field (e.g., "Alex" instead of "Alexander")
2. **First Name Only**: Uses \`first_name\` field (e.g., "Alexander")
3. **Full Name**: Uses \`first_name + last_name\` (e.g., "Alexander Johnson")

**How it works:**
- Employee sets preference in their profile
- Name displays according to preference in:
  - Organization employee lists
  - Practitioner client dropdowns
  - Session acknowledgments
  - Admin dashboards

**Admins cannot override employee name preferences.**

### Viewing Employee Session History

1. Navigate to **Employees** tab
2. Click on employee name
3. View **"Session History"** tab

**Data visible to admins:**
- Session date and title
- Session duration
- Category
- Credits consumed
- Delivery status

**Data NOT visible to admins (privacy protected):**
- Session content
- Employee biography
- Session feedback
- Personal notes

**Why privacy matters:**
VoiceThera protects employee wellness data as confidential. Admins see usage for billing and program evaluation, but not therapeutic content.

---

## Credit Management

### Bulk Credit Model (No Subscriptions)

Organizations do **not** have subscriptions. Instead:

✅ **Purchase credits in bulk** via enterprise credit packs
✅ **Allocate monthly allowances** to individual employees
✅ **Unallocated credits** remain in organizational reserve
✅ **Credits never expire** (organization level)

### Enterprise Credit Pack Tiers

| Pack | Price | Credits | Cost Per Credit | Est. 10-Min Sessions |
|------|-------|---------|-----------------|----------------------|
| **Starter Org** | $250 | 2,500 | $0.10 | 125 sessions |
| **Growth Org** | $450 | 5,000 | $0.09 | 250 sessions |
| **Business Org** | $850 | 10,000 | $0.085 | 500 sessions |
| **Enterprise Org** | $1,500 | 20,000 | $0.075 | 1,000 sessions |
| **Enterprise Plus** | $2,500 | 35,000 | $0.071 | 1,750 sessions |
| **Corporate** | $4,000 | 60,000 | $0.067 | 3,000 sessions |
| **Corporate Plus** | $6,000 | 100,000 | $0.060 | 5,000 sessions |
| **Enterprise Max** | $8,000 | 150,000 | $0.053 | 7,500 sessions |

**Volume discounts**: Larger packs offer better per-credit pricing.

### Purchasing Credits

1. Navigate to **Credits & Usage** tab
2. Click **"Purchase Credits"**
3. Select credit pack tier
4. Complete payment via Stripe
5. Credits added to organizational balance immediately

**Payment methods**: Credit card, ACH transfer (for Enterprise Max)

### Employee Allowance System

**How allowances work:**

1. Admin sets monthly allowance for each employee (e.g., 50 credits/month)
2. Allowances deducted from organizational balance
3. Employee can create sessions up to their allowance limit
4. Unused allowances expire at month-end (credits return to unallocated reserve)
5. Allowances reset on employee's reset date

**Setting allowances:**

1. Navigate to **Employees** tab
2. Click employee name
3. Scroll to **"Credit Allowance"** section
4. Set:
   - **Monthly Allowance** (credits per month)
   - **Reset Date** (day of month allowances reset, e.g., 1st)
5. Click **"Save Allowance"**

**Example:**
- Employee: Jane Doe
- Monthly Allowance: 50 credits
- Reset Date: 1st of month
- Jane can create 25 minutes of sessions per month (50 credits ÷ 2 credits/min)

### Bulk Allowance Setting

For organizations with many employees:

1. Navigate to **Credits & Usage** tab
2. Click **"Bulk Set Allowances"**
3. Choose:
   - Apply to all employees OR specific department
   - Monthly allowance amount (e.g., 40 credits)
   - Reset date (e.g., 1st)
4. Confirm

**Use case**: Set standard allowance for all employees, then adjust individuals as needed.

### Unallocated Reserve

Credits not assigned as employee allowances remain in reserve:

**Reserve uses:**
- Buffer for new hires
- Bonus allocations during high-stress periods
- Practitioner session coverage (if using external practitioners)

**Example calculation:**
- Total credits purchased: 10,000
- 50 employees × 40 credits/month = 2,000 credits allocated
- Unallocated reserve: 8,000 credits

### Viewing Credit Balance

**Credits & Usage Dashboard shows:**
- **Total Credits Purchased**: Lifetime total
- **Credits Balance**: Current available (allocated + unallocated)
- **Allocated This Month**: Total employee allowances
- **Unallocated Reserve**: Credits not assigned
- **Credits Used This Month**: Consumption since last reset
- **Burn Rate**: Average credits per day

**Alerts:**
- ⚠️ **Low Credit Warning**: Balance below 500 credits
- 🔴 **Critical**: Balance below 100 credits

---

## Practitioner Access Control

### Understanding Practitioner Access

**Why access control?**

External practitioners (not employees) need explicit permission to:
- View your organization in their client form dropdowns
- See your employee list
- Link clients to your organization employees
- Create sessions that charge your organization's credit pool

**Access grants are:**
- Organization-initiated (practitioners cannot self-request)
- Granular (per-practitioner basis)
- Auditable (full access logs)
- Revocable (can be revoked anytime)

### Granting Practitioner Access

1. Navigate to **Settings** tab → **Practitioner Access** sub-tab
2. Click **"Grant Access"**
3. Enter:
   - **Practitioner Email** (must have VoiceThera practitioner account)
   - **Access Level**: "full" (default, only option currently)
   - **Expiration Date** (optional) - access auto-revokes after this date
   - **Notes** (optional) - reason for access grant
4. Click **"Grant Access"**

**System checks:**
- Email must belong to existing practitioner account
- Practitioner cannot already have active access
- One active grant per practitioner (must revoke before re-granting)

**Practitioner notification:**
Practitioner receives email notification of access grant with details.

### Setting Expiration Dates

**Use cases for expiration:**
- Temporary contractor relationships
- Trial period with practitioner
- Project-based wellness programs
- Compliance requirements

**How it works:**
- Access automatically revokes at midnight on expiration date
- Practitioner loses visibility of organization and employees
- Existing sessions remain accessible
- No credits deducted after expiration

**Example:**
- Grant access: January 1, 2025
- Expiration: March 31, 2025
- Access auto-revokes: April 1, 2025 at 12:00am

### Viewing Active Access Grants

**Practitioner Access table shows:**
- Practitioner name and email
- Granted date
- Expiration date (if set)
- Status (active, expired, revoked)
- Last session created (if any)
- Total sessions created

### Revoking Access

To immediately remove practitioner access:

1. Navigate to **Settings** → **Practitioner Access**
2. Find practitioner in list
3. Click **"Revoke Access"**
4. Confirm revocation

**Effects:**
- Immediate loss of organization visibility
- Cannot create new sessions for org-linked clients
- Existing sessions remain accessible
- Practitioner notified of revocation

**Note**: Revocation is permanent for that grant. To restore access, create new grant.

### Audit Trail

All access activity logged:
- Access granted (by whom, when)
- Access revoked (by whom, when)
- Sessions created (practitioner, date, client, credits)
- Access expiration (automatic)

**Accessing audit logs:**
1. Navigate to **Credits & Usage** → **Transaction Logs** sub-tab
2. Filter by **"Practitioner Sessions"**
3. See full history

---

## Usage Analytics

### Real-Time Dashboard

**Credits & Usage tab provides:**

**Overview Metrics:**
- Total sessions created this month
- Credits used vs. remaining
- Average session length
- Participation rate (% of employees who created sessions)

**Burn Rate:**
- Credits consumed per day
- Projected depletion date at current rate
- Recommendations for credit purchases

**Per-Employee Breakdown:**

| Employee | Allowance | Used | Remaining | Last Session |
|----------|-----------|------|-----------|--------------|
| Jane Doe | 50 | 32 | 18 | 2 days ago |
| John Smith | 50 | 48 | 2 | 1 day ago |
| Sarah Lee | 50 | 0 | 50 | Never |

**Insights:**
- Identify high utilizers (engaged employees)
- Identify non-utilizers (outreach opportunities)
- Adjust allowances based on usage patterns

### Transaction Logs

**View detailed transaction history:**

**Employee Sessions:**
- Employee name
- Session date and title
- Duration and credits consumed
- Category

**Practitioner Sessions:**
- Practitioner name
- Client name (org-linked employee)
- Session date and title
- Credits consumed
- Transaction type: "practitioner_session"

**Export options:**
- Download CSV for accounting
- Filter by date range, employee, or practitioner
- Generate monthly reports for leadership

### Department Analytics

**View wellness program engagement by department:**

1. Navigate to **Departments** tab
2. Click department name
3. View **"Analytics"** sub-tab

**Department metrics:**
- Total employees in department
- Participation rate
- Average sessions per employee
- Total credits consumed
- Most popular session categories

**Use cases:**
- Identify departments needing wellness outreach
- Tailor program communications by department
- Evaluate ROI of wellness initiatives

---

## Context Switching (Employee Experience)

### How Employees Use Organization Accounts

Employees can operate in two modes:

**Personal Mode:**
- Uses personal credit pool (their own subscription or purchased credits)
- No organization context in sessions
- Completely private from organization

**Employee Mode:**
- Uses organization credit pool (monthly allowance)
- Organization context integrated into sessions
- Sessions acknowledge organizational support and values
- Tracked in organization analytics

### UI for Context Switching

Employees see a **context switcher** in their dashboard:

**Options:**
- 👤 **Personal** (using my credits)
- 🏢 **[Organization Name]** (using work credits)

When in employee mode, session generation:
- Blends personal and organization biography
- Opens with org acknowledgment
- Connects personal struggles to organizational values
- Consumes organization credits

### Admin Visibility

Admins see:
- ✅ Sessions created in employee mode (usage analytics)
- ❌ Sessions created in personal mode (completely private)

**Privacy boundary respected**: Employees can use personal mode for sessions they don't want tracked by organization.

---

## Security & Privacy

### Data Protection

VoiceThera handles employee wellness data as **protected health information**:

**Data encrypted:**
- In transit (TLS 1.3)
- At rest (AES-256)

**Access controls:**
- Role-based access (admin, employee, practitioner)
- Row-level security (RLS) policies
- Audit logging of all access

**Compliance:**
- Business Associate Agreement (BAA) available for Enterprise plans
- Secure infrastructure
- Regular security audits

### Data Ownership

**Organization owns:**
- Organization profile (biography, values, mission)
- Department structures
- Credit purchase records
- Usage analytics (aggregated)

**Employee owns:**
- Personal biography
- Session content
- Session feedback
- Wellness data

**Practitioners own:**
- Client notes (for org-linked clients)
- Therapeutic methodologies

**Shared ownership:**
- Organization-contextualized sessions (both org and employee)

### Data Retention

**Active employees:**
- All data retained indefinitely

**Departing employees:**
- Employee record retained for 90 days after deactivation
- Sessions remain accessible to employee
- Organization loses access to employee-specific data
- Anonymous analytics retained for program evaluation

**Account deletion:**
- Employee can request full data deletion
- Organization must comply within 30 days
- Anonymized analytics may be retained

---

## Frequently Asked Questions

### Setup

**Q: How many admins can an organization have?**
A: Unlimited. Owner can add multiple admins with full or limited permissions.

**Q: Can I transfer ownership to another admin?**
A: Yes, navigate to Settings → Admins → Transfer Ownership.

**Q: Can I delete my organization account?**
A: Yes, but all employee access will be revoked and credits forfeited. Contact support for assistance.

### Credits

**Q: Do organization credits expire?**
A: No, purchased credits remain in your pool indefinitely.

**Q: What happens to unused employee allowances?**
A: They expire at month-end and return to unallocated reserve.

**Q: Can employees purchase personal credits?**
A: Yes, employees can maintain personal credit pools separate from organization allowances.

**Q: What if an employee exceeds their allowance?**
A: Session creation fails. Admin must increase allowance or wait until next reset.

### Employees

**Q: Can employees leave the organization voluntarily?**
A: Employees can deactivate their organization membership in their settings. Admins can also deactivate employees.

**Q: What happens to sessions when an employee leaves?**
A: Employee retains access to their sessions. Organization loses access to employee-specific data after 90 days.

**Q: Can I see employee session content?**
A: No, session content is private. You see usage analytics only (date, duration, category, credits).

### Practitioners

**Q: Why do practitioners need explicit access?**
A: To protect employee privacy and control which external parties can charge your credit pool.

**Q: Can practitioners see all my employees?**
A: Only employees in your organization. Practitioners cannot see other organizations' employees.

**Q: Can I limit practitioner access to specific departments?**
A: Not currently. Access grants are organization-wide. Department-level access is a roadmap feature.

---

## Support & Resources

**For organization setup help:**
- Email: enterprise@voicethera.com
- Chat: In-app support (Settings → Help)
- Phone: Request callback for Enterprise Max plans

**For billing questions:**
- Email: billing@voicethera.com

**Related guides:**
- Credit System & Billing Guide
- Session Generation Features Guide
- Client Management & Organization Linking Guide (for practitioners)

**Last Updated**: January 2025
**Version**: 1.0
`;
